/**
 * AdminCityDemandHeatmap — visual demand/supply gap report for admin.
 *
 * Each row shows a service city with:
 *   - demand  (customer searches in last N days)
 *   - supply  (live vendors + pros covering that city)
 *   - gap     (max(0, demand - supply)) — coloured red when positive
 *   - coverage bar (supply / demand, capped at 100%)
 *
 * Sorted by gap desc so undersupplied cities surface at the top — admin
 * uses this to decide which cities to push vendor recruitment for.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartBar, TrendUp, Warning } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const AdminCityDemandHeatmap = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ rows: [], total_searches: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHeatmap(); }, [days]);

  const fetchHeatmap = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${BACKEND_URL}/api/admin/city-demand-heatmap?days=${days}`,
        { withCredentials: true },
      );
      setData(d || { rows: [], total_searches: 0 });
    } catch (err) {
      console.error('Failed to load heatmap', err);
    }
    setLoading(false);
  };

  const maxDemand = Math.max(1, ...data.rows.map((r) => r.demand || 0));

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="city-demand-heatmap-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <ChartBar size={20} className="text-brand-primary" /> City Demand Heatmap
            </CardTitle>
            <CardDescription>
              Where customers are searching vs. how many vendors cover them. Rows with a
              <span className="font-semibold text-red-600"> red gap</span> need more vendor recruitment.
            </CardDescription>
          </div>
          <div className="flex gap-1 border border-[#E8E1D5] rounded-sm overflow-hidden" data-testid="heatmap-window-switch">
            {WINDOWS.map((w) => (
              <button
                key={w.label}
                type="button"
                onClick={() => setDays(w.days)}
                className={`px-3 py-1 text-xs font-bold ${
                  days === w.days ? 'bg-brand-primary text-sand-50' : 'bg-white text-text-secondary hover:bg-brand-primary/5'
                }`}
                data-testid={`heatmap-window-${w.label}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-1" data-testid="heatmap-total">
          <TrendUp size={12} className="inline mr-1" />
          <span className="font-bold">{data.total_searches}</span> customer search(es) in the last {days} days
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary italic">Loading heatmap…</p>
        ) : data.rows.length === 0 ? (
          <p className="text-sm text-text-secondary italic">No service cities configured yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="heatmap-table">
              <thead>
                <tr className="border-b border-[#E8E1D5] text-xs font-bold tracking-wider uppercase text-text-secondary">
                  <th className="text-left py-2 pr-3">City</th>
                  <th className="text-right py-2 px-3">Demand</th>
                  <th className="text-right py-2 px-3">Supply</th>
                  <th className="text-right py-2 px-3">Gap</th>
                  <th className="text-left py-2 pl-3 min-w-[160px]">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const widthPct = Math.min(100, Math.round((r.demand / maxDemand) * 100));
                  const coveragePct = Math.min(100, Math.round((r.coverage || 0) * 100));
                  const hot = r.gap > 0;
                  return (
                    <tr key={r.city} className="border-b border-[#E8E1D5]/60 hover:bg-brand-primary/5"
                      data-testid={`heatmap-row-${r.city}`}>
                      <td className="py-2 pr-3 font-medium flex items-center gap-1">
                        {hot && <Warning size={14} className="text-red-500" />}
                        {r.city}
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{r.demand}</td>
                      <td className="py-2 px-3 text-right font-mono">{r.supply}</td>
                      <td className={`py-2 px-3 text-right font-mono font-bold ${hot ? 'text-red-600' : 'text-emerald-700'}`}
                        data-testid={`heatmap-gap-${r.city}`}>
                        {r.gap > 0 ? `+${r.gap}` : '—'}
                      </td>
                      <td className="py-2 pl-3">
                        <div className="relative h-3 w-full bg-gray-100 rounded overflow-hidden">
                          {/* demand bar (gray) */}
                          <div className="absolute inset-y-0 left-0 bg-gray-300"
                            style={{ width: `${widthPct}%` }} />
                          {/* coverage bar (green/amber based on coverage) */}
                          <div className={`absolute inset-y-0 left-0 ${coveragePct >= 100 ? 'bg-emerald-500' : coveragePct >= 50 ? 'bg-amber-400' : 'bg-red-500'}`}
                            style={{ width: `${Math.round((r.supply / maxDemand) * 100)}%` }} />
                        </div>
                        <p className="text-[10px] text-text-secondary mt-0.5">
                          {r.demand > 0 ? `${coveragePct}% covered` : 'No searches'}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] text-text-secondary">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-gray-300 rounded-sm" /> Demand</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-500 rounded-sm" /> 100%+ covered</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-amber-400 rounded-sm" /> 50–99% covered</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-red-500 rounded-sm" /> &lt;50% covered</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminCityDemandHeatmap;
